import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Plus, Search, User, Building, FileText, Calendar, AlertCircle } from "lucide-react";
import "./Transfer.css";

const url = process.env.REACT_APP_BACKEND_URL;

const Transfer = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    original_contract_id: "",
    original_customer_id: "",
    original_client_id: "",
    original_job_id: "",
    original_contract_start: "",
    original_contract_end: "",
    new_client_id: "",
    new_contract_start: "",
    new_contract_end: "",
    transfer_reason: "",
    remarks: "",
    transfer_job_id: "",
    transfer_job_name: "",
    transfer_job_section: ""
  });

  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedNewClient, setSelectedNewClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [fetchingContracts, setFetchingContracts] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchClients();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${url}/api/customer/all`);
      console.log("✅ Customers fetched:", response.data.data.length);
      setCustomers(response.data.data);
    } catch (error) {
      console.error("❌ Failed to fetch customers:", error);
      toast.error("Failed to fetch customers");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${url}/api/client/all`);
      console.log("✅ Clients fetched:", response.data.data.length);
      setClients(response.data.data);
    } catch (error) {
      console.error("❌ Failed to fetch clients:", error);
      toast.error("Failed to fetch clients");
    }
  };

  const fetchCustomerContracts = async (customerId) => {
    setFetchingContracts(true);
    try {
      console.log("🔍 Fetching contracts for customer ID:", customerId);
      
      const response = await axios.get(`${url}/api/transfer/customer/${customerId}/contracts`);
      console.log("📦 API Response:", response.data);
      
      if (response.data.success) {
        const contractsData = response.data.data;
        const customerInfo = response.data.customerInfo;
        
        console.log(`📊 Found ${contractsData.length} active contract(s) for ${customerInfo?.name}`);
        
        setContracts(contractsData);
        
        if (contractsData.length === 0) {
          toast.info(`ℹ️ No active contracts found for ${customerInfo?.name}. Please create a contract first.`);
        } else {
          toast.success(`✅ Found ${contractsData.length} active contract(s) for ${customerInfo?.name}`);
          
          // Log each contract for debugging
          contractsData.forEach((contract, idx) => {
            console.log(`  Contract ${idx + 1}: #${contract.no} - Client: ${contract.client_id?.name} - Job: ${contract.job_id?.category_name} - Section: ${contract.job_id?.section || contract.section}`);
          });
        }
      }
    } catch (error) {
      console.error("❌ Failed to fetch contracts:", error);
      toast.error("Failed to fetch contracts");
      setContracts([]);
    } finally {
      setFetchingContracts(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    console.log("=========================================");
    console.log("👤 Customer Selected:", customer.fullname);
    console.log("Customer ID:", customer._id);
    
    // Clear all previous selections
    setSelectedContract(null);
    setSelectedNewClient(null);
    setContracts([]);
    
    // Reset form data for contract-related fields
    setFormData({
      ...formData,
      original_contract_id: "",
      original_customer_id: customer._id,
      original_client_id: "",
      original_job_id: "",
      original_contract_start: "",
      original_contract_end: "",
      new_client_id: "",
      new_contract_start: "",
      new_contract_end: "",
      transfer_reason: "",
      remarks: "",
      transfer_job_id: "",
      transfer_job_name: "",
      transfer_job_section: ""
    });
    
    // Set selected customer
    setSelectedCustomer(customer);
    
    // Fetch contracts ONLY for this customer
    fetchCustomerContracts(customer._id);
  };

  const handleContractSelect = (contract) => {
    console.log("=========================================");
    console.log("📄 Contract Selected:", contract.no);
    console.log("Contract Client:", contract.client_id?.name);
    console.log("Contract Job ID:", contract.job_id?._id);
    console.log("Contract Job Name:", contract.job_id?.category_name);
    console.log("Contract Job Section:", contract.job_id?.section || contract.section);
    
    // Verify this contract belongs to the selected customer
    if (contract.cust_id !== selectedCustomer?._id) {
      console.error("❌ Contract doesn't belong to selected customer!");
      toast.error("This contract does not belong to the selected customer");
      return;
    }
    
    setSelectedContract(contract);
    setFormData(prev => ({
      ...prev,
      original_contract_id: contract._id,
      original_client_id: contract.client_id?._id,
      original_job_id: contract.job_id?._id,
      original_contract_start: contract.contract_start,
      original_contract_end: contract.contract_end,
      // Capture job information for transfer
      transfer_job_id: contract.job_id?._id,
      transfer_job_name: contract.job_id?.category_name || "",
      transfer_job_section: contract.job_id?.section || contract.section || ""
    }));
    
    toast.success(`✅ Contract #${contract.no} selected with job: ${contract.job_id?.category_name || "N/A"}`);
  };

  const handleNewClientSelect = (client) => {
    console.log("=========================================");
    console.log("🏢 New Client Selected:", client.name);
    console.log("Client ID:", client._id);
    console.log("Client Salary:", client.salary);
    
    setSelectedNewClient(client);
    setFormData(prev => ({
      ...prev,
      new_client_id: client._id
    }));
    
    toast.success(`✅ ${client.name} selected as new client`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("=========================================");
    console.log("🚀 Form Submission Started");
    console.log("Form Data:", formData);
    console.log("Selected Customer:", selectedCustomer?.fullname);
    console.log("Selected Contract:", selectedContract?.no);
    console.log("Selected New Client:", selectedNewClient?.name);
    console.log("Transfer Job Name:", formData.transfer_job_name);
    console.log("Transfer Job Section:", formData.transfer_job_section);
    
    // Validate all required fields
    if (!formData.original_contract_id) {
      console.error("❌ Missing original_contract_id");
      toast.error("Please select a contract to transfer");
      return;
    }
    
    if (!formData.original_customer_id) {
      console.error("❌ Missing original_customer_id");
      toast.error("Please select a customer");
      return;
    }
    
    if (!formData.new_client_id) {
      console.error("❌ Missing new_client_id");
      toast.error("Please select a new client");
      return;
    }
    
    if (!formData.new_contract_start) {
      console.error("❌ Missing new_contract_start");
      toast.error("Please enter contract start date");
      return;
    }
    
    if (!formData.new_contract_end) {
      console.error("❌ Missing new_contract_end");
      toast.error("Please enter contract end date");
      return;
    }
    
    // Validate dates
    const startDate = new Date(formData.new_contract_start);
    const endDate = new Date(formData.new_contract_end);
    
    if (startDate >= endDate) {
      console.error("❌ Invalid dates: Start date must be before end date");
      toast.error("Contract start date must be before end date");
      return;
    }
    
    console.log("✅ All validations passed, submitting...");
    setLoading(true);

    try {
      console.log("📤 Sending POST request to:", `${url}/api/transfer`);
      console.log("Request payload:", JSON.stringify(formData, null, 2));
      
      const response = await axios.post(`${url}/api/transfer`, formData);
      console.log("📥 Response:", response.data);

      if (response.data.success) {
        toast.success("🎉 Transfer completed successfully!");
        console.log("✅ Transfer successful, navigating to ViewTransfer");
        setTimeout(() => {
          navigate("/ViewTransfer");
        }, 1000);
      } else {
        toast.error(response.data.message || "Transfer failed");
      }
    } catch (error) {
      console.error("❌ Transfer error:", error);
      console.error("Error response:", error.response?.data);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Transfer failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.fullname?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.phone?.includes(customerSearch)
  );

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(clientSearch.toLowerCase()) &&
    client._id !== formData.original_client_id &&
    client.status === "active"
  );

  return (
    <div className="transfer-wrapper">
      <div className="transfer-container">
        <h2 className="transfer-title">
          <ArrowLeftRight size={24} /> Customer Transfer
        </h2>

        <div className="transfer-header">
          <button
            onClick={() => navigate('/ViewTransfer')}
            className="view-button"
          >
            <Plus size={16} /> View Transfers
          </button>
        </div>

        <form className="transfer-form" onSubmit={handleSubmit}>
          {/* Step 1: Select Customer */}
          <div className="form-section">
            <h3>
              <User size={20} /> Step 1: Select Customer
            </h3>
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search customer by name or phone..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
            </div>
            <div className="items-list">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map(customer => (
                  <div
                    key={customer._id}
                    className={`item-card ${selectedCustomer?._id === customer._id ? 'selected' : ''}`}
                    onClick={() => handleCustomerSelect(customer)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="item-name">{customer.fullname}</div>
                    <div className="item-details">
                      <span>{customer.phone}</span>
                      <span>{customer.email}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <AlertCircle size={20} />
                  <p>No customers found</p>
                </div>
              )}
            </div>
          </div>

          {/* Step 2: Select Contract */}
          {selectedCustomer && (
            <div className="form-section">
              <h3>
                <FileText size={20} /> Step 2: Select Contract to Transfer
              </h3>
              {fetchingContracts ? (
                <div className="loading-contracts">
                  <div className="spinner"></div>
                  <p>Loading contracts...</p>
                </div>
              ) : contracts.length > 0 ? (
                <div className="items-list">
                  {contracts.map(contract => (
                    <div
                      key={contract._id}
                      className={`item-card ${selectedContract?._id === contract._id ? 'selected' : ''}`}
                      onClick={() => handleContractSelect(contract)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="item-name">
                        Contract #{contract.no}
                        {contract.status !== "active" && (
                          <span className="contract-status-badge inactive">
                            ({contract.status})
                          </span>
                        )}
                      </div>
                      <div className="item-details">
                        <span>Client: {contract.client_id?.name || 'N/A'}</span>
                        <span>Job: {contract.job_id?.category_name || 'N/A'}</span>
                        <span>Section: {contract.job_id?.section || contract.section || 'N/A'}</span>
                        <span>Start: {new Date(contract.contract_start).toLocaleDateString()}</span>
                        <span>End: {new Date(contract.contract_end).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <AlertCircle size={20} />
                  <p>No active contracts found for {selectedCustomer.fullname}</p>
                  <button 
                    type="button"
                    onClick={() => navigate('/AddContract')}
                    className="create-contract-btn"
                  >
                    Create Contract
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select New Client */}
          {selectedContract && (
            <div className="form-section">
              <h3>
                <Building size={20} /> Step 3: Select New Client
              </h3>
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search new client..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
              </div>
              <div className="items-list">
                {filteredClients.length > 0 ? (
                  filteredClients.map(client => (
                    <div
                      key={client._id}
                      className={`item-card ${selectedNewClient?._id === client._id ? 'selected' : ''}`}
                      onClick={() => handleNewClientSelect(client)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="item-name">{client.name}</div>
                      <div className="item-details">
                        <span>Location: {client.location || 'N/A'}</span>
                        <span>Capacity: {client.no_of_capacity}</span>
                        <span>Salary: ${client.salary}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <AlertCircle size={20} />
                    <p>No active clients found</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Transfer Details */}
          {selectedNewClient && (
            <>
              <div className="form-section">
                <h3>
                  <Calendar size={20} /> Step 4: Transfer Details
                </h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>New Contract Start Date *</label>
                    <input
                      type="date"
                      name="new_contract_start"
                      value={formData.new_contract_start}
                      onChange={handleChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="form-group">
                    <label>New Contract End Date *</label>
                    <input
                      type="date"
                      name="new_contract_end"
                      value={formData.new_contract_end}
                      onChange={handleChange}
                      required
                      min={formData.new_contract_start || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Transfer Reason</label>
                  <textarea
                    name="transfer_reason"
                    placeholder="Please provide reason for transfer..."
                    value={formData.transfer_reason}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Additional Remarks</label>
                  <textarea
                    name="remarks"
                    placeholder="Any additional notes..."
                    value={formData.remarks}
                    onChange={handleChange}
                    rows="2"
                  />
                </div>
              </div>

              {/* Transfer Summary */}
              <div className="transfer-summary">
                <h3>Transfer Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <strong>Customer:</strong>
                    <span>{selectedCustomer?.fullname}</span>
                  </div>
                  <div className="summary-item">
                    <strong>From Client:</strong>
                    <span>{selectedContract?.client_id?.name}</span>
                  </div>
                  <div className="summary-item">
                    <strong>To Client:</strong>
                    <span className="new-client-highlight">{selectedNewClient?.name}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Job Title:</strong>
                    <span>{selectedContract?.job_id?.category_name || formData.transfer_job_name || "N/A"}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Job Section:</strong>
                    <span>{selectedContract?.job_id?.section || selectedContract?.section || formData.transfer_job_section || "N/A"}</span>
                  </div>
                  <div className="summary-item">
                    <strong>Original Contract:</strong>
                    <span>#{selectedContract?.no}</span>
                  </div>
                  <div className="summary-item">
                    <strong>New Salary:</strong>
                    <span className="salary-amount">${selectedNewClient?.salary}</span>
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Processing Transfer...
                  </>
                ) : (
                  "Complete Transfer"
                )}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Transfer;